import { useState } from 'react';
import { cn } from '../../utils/utils';
import LabelWithHelp from '../LabelWithHelp';

interface InputFieldProps {
    value?: string;
    onChange?: (value: string) => void;
    onBlur?: () => void;
    label: string;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    type?: string;
    /** Trailing inline text (e.g. a unit like "EUR"). Visual only —
     *  value stays numeric/textual and is not affected. */
    suffix?: string;
    /** Right-align the input text. Useful for numeric fields so a
     *  trailing suffix sits right next to the value instead of at
     *  the far end of a wide input. */
    alignRight?: boolean;
    /** Forwarded to the underlying input — useful on type="number". */
    min?: number | string;
    max?: number | string;
    step?: number | string;
    /** Maximum number of characters the user can type (native input cap). */
    maxLength?: number;
    /** For type="number": block keys that would let the user enter
     *  "e", "+", "-", "." etc. Otherwise Chrome keeps the character
     *  visible in the DOM even though input.value is empty, leaving
     *  the floating label in a confused state. */
    integerOnly?: boolean;
    /** Mark the field as invalid: red border + optional error text below. */
    error?: boolean;
    /** Inline error message rendered under the input when `error` is true. */
    errorText?: string;
    /** Render the label as a separate block above the input rather than
     *  the floating-label-inside pattern. Matches the convention used in
     *  newer admin-ui sections (e.g. AccommodationSection edit mode). */
    labelAbove?: boolean;
    /** Short explanation rendered as an (i) tooltip next to the label, via the
     *  shared `LabelWithHelp`. Use it only where the field is genuinely
     *  non-obvious. Only honoured in `labelAbove` mode. */
    help?: string;
    /** Static text shown inside the field before the input value, like
     *  the "app.kolula.com/" prefix on the onboarding slug field. The
     *  field looks like one continuous bordered container; users see
     *  prefix + their value as a live URL preview. Only honoured in
     *  `labelAbove` mode. */
    prefix?: string;
}

type SharedInputProps = Readonly<InputFieldProps & {
    focused: boolean;
    setFocused: (focused: boolean) => void;
    handleKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}>;

const integerOnlyKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    // Let control keys through (backspace, arrows, tab, enter…)
    if (e.key.length > 1 || e.ctrlKey || e.metaKey) return;
    if (!/^\d$/.test(e.key)) e.preventDefault();
};

const PrefixedField = ({
    value, onChange, label, placeholder, disabled, className, type,
    alignRight, min, max, step, maxLength, prefix,
    focused, setFocused, handleKeyDown,
}: SharedInputProps) => (
    <div className={cn('flex flex-col gap-1.5', className)}>
        <label className={'text-sm text-foreground/70 font-medium'}>{label}</label>
        <div
            className={cn(
                'flex items-center py-2 px-4 rounded-xl bg-transparent w-full',
                'border transition-all',
                focused ? 'border-primary' : 'border-foreground',
                disabled && 'cursor-not-allowed',
            )}
        >
            <span className={'text-foreground/60 select-none whitespace-nowrap pr-0.5'}>
                {prefix}
            </span>
            <input
                className={cn(
                    'flex-1 min-w-0 outline-none bg-transparent text-foreground',
                    alignRight && 'text-right',
                )}
                type={type}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder={focused ? '' : placeholder}
                disabled={disabled}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={handleKeyDown}
                min={min}
                max={max}
                step={step}
                maxLength={maxLength}
            />
        </div>
    </div>
);

const LabelAboveField = ({
    value, onChange, label, placeholder, disabled, className, type,
    suffix, alignRight, min, max, step, maxLength, help,
    focused, setFocused, handleKeyDown,
}: SharedInputProps) => (
    <div className={cn('flex flex-col gap-1.5', className)}>
        {help ? (
            <LabelWithHelp text={label} help={help} className={'mb-0'} />
        ) : (
            <label className={'text-sm text-foreground/70 font-medium'}>{label}</label>
        )}
        <div className={'relative'}>
            <input
                className={cn(
                    'py-2 px-4 rounded-xl text-foreground bg-transparent w-full',
                    'border transition-all outline-none',
                    focused ? 'border-primary' : 'border-foreground',
                    disabled && 'cursor-not-allowed',
                    suffix && 'pr-14',
                    alignRight && 'text-right',
                )}
                type={type}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder={placeholder}
                disabled={disabled}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                onKeyDown={handleKeyDown}
                min={min}
                max={max}
                step={step}
                maxLength={maxLength}
            />
            {suffix && (
                <span
                    className={cn(
                        'absolute right-4 top-1/2 -translate-y-1/2',
                        'text-sm text-foreground/60 pointer-events-none select-none',
                    )}
                >
                    {suffix}
                </span>
            )}
        </div>
    </div>
);

const FloatingLabelField = ({
    value, onChange, onBlur, label, placeholder, disabled, className, type,
    suffix, alignRight, min, max, step, maxLength,
    error, errorText,
    focused, setFocused, handleKeyDown,
}: SharedInputProps) => {
    const isFloating = focused || !!value;
    return (
        <div className={cn('relative', className)}>
            <input
                className={cn(
                    'py-2 px-4 rounded-xl text-foreground bg-transparent w-full',
                    'border transition-all outline-none',
                    error
                        ? 'border-red-500'
                        : focused
                            ? 'border-primary'
                            : 'border-foreground',
                    disabled && 'cursor-not-allowed',
                    suffix && 'pr-14',
                    alignRight && 'text-right',
                )}
                type={type}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder={isFloating ? placeholder : ''}
                disabled={disabled}
                onFocus={() => setFocused(true)}
                onBlur={() => {
                    setFocused(false);
                    onBlur?.();
                }}
                onKeyDown={handleKeyDown}
                min={min}
                max={max}
                step={step}
                maxLength={maxLength}
            />
            {suffix && (
                <span
                    className={cn(
                        'absolute right-4 top-1/2 -translate-y-1/2',
                        'text-sm text-foreground/60 pointer-events-none select-none',
                    )}
                >
                    {suffix}
                </span>
            )}
            <label
                className={cn(
                    'absolute left-3 pointer-events-none transition-all duration-200',
                    'truncate max-w-[calc(100%-1.5rem)]',
                    isFloating
                        ? '-top-2.5 text-xs bg-white px-1 text-foreground'
                        : 'top-1/2 -translate-y-1/2 text-sm text-foreground',
                )}
            >
                {label}
            </label>
            {error && errorText && (
                <p className={'mt-1 text-xs text-red-600'}>{errorText}</p>
            )}
        </div>
    );
};

const InputField = (props: Readonly<InputFieldProps>) => {
    const [focused, setFocused] = useState(false);
    const handleKeyDown = props.integerOnly ? integerOnlyKeyDown : undefined;
    const shared: SharedInputProps = { ...props, focused, setFocused, handleKeyDown };

    if (props.labelAbove && props.prefix) return <PrefixedField {...shared} />;
    if (props.labelAbove) return <LabelAboveField {...shared} />;
    return <FloatingLabelField {...shared} />;
};

export default InputField;
